import React, { useEffect, useRef, useState } from 'react'
import { Card, Col, Overlay, Table, Tooltip } from 'react-bootstrap'
import { genderSymbols, isTabletOrMobileQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import qs from 'qs'
import { OverlayInjectedProps } from 'react-bootstrap/Overlay'
import { useAppSelector } from '../../hooks'

const ParticipantData = () => {
  const { data: institution } = useAppSelector(
    (state) => state.institution,
  )
  const { data: program } = useAppSelector((state) => state.program)
  const { data: participant } = useAppSelector(
    (state) => state.participant,
  )
  const { baseUrl } = useAppSelector((state) => state.misc)

  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery)

  const [emailFailed, setEmailFailed] = useState(false)
  const [showEmailWarning, setShowEmailWarning] = useState(false)
  const emailField = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const validateEmail = async () => {
      console.log('Sending email request to server...')
      const result = await axios({
        method: 'POST',
        url: `${baseUrl}/api/custom/validate/email/`,
        data: qs.stringify({
          email: participant.email,
        }),
      })
      console.log(`Email status is: ${result.data.status}`)
      return result.data.status !== 'valid'
    }
    validateEmail().then((hasFailed) => setEmailFailed(hasFailed))
  }, [baseUrl, participant.email])

  return (
    <>
      {!isTabletOrMobile ? (
        <Table responsive striped bordered>
          <tbody>
          <tr>
            <th>Name:</th>
            <td>
              {`${participant.first_name} ${participant.last_name} ${
                program.gender_selection_needed ? ` ${genderSymbols[participant.gender]}` : ""
              }`}
            </td>
          </tr>
          <tr>
            <th>Adresse:</th>
            <td>
              {participant.street}
              <br />
              {participant.zip_code} {participant.city}{' '}
              {participant.district && '(' + participant.district + ')'}
            </td>
          </tr>
          <tr>
            <th>Geburtsdatum:</th>
            <td>{participant.date_of_birth}</td>
          </tr>
          {participant.disabled && (
            <tr>
              <th>Behinderung/Beeinträchtigung:</th>
              <td>
                {participant.disabled ? participant.handicap || 'Ja' : ''}
                <br />
                <small className="text-muted">Angabe hat keinen Einfluss auf die Platzvergabe</small>
              </td>
            </tr>
          )}
          <tr>
            <th>E-Mail:</th>
            <td className={emailFailed ? 'text-red' : 'text-black'}>
              {emailFailed ? (
                <>
                  <small>
                      <span
                        className='text-danger font-weight-bold'
                        role={'button'}
                        id='email-warning'
                        ref={emailField}
                        onClick={() => setShowEmailWarning(!showEmailWarning)}
                      >
                        {participant.email}
                      </span>
                    {emailField && emailField.current && (
                      <Overlay
                        target={emailField.current}
                        show={showEmailWarning}
                        placement='left'
                      >
                        {(props: OverlayInjectedProps) => (
                          <Tooltip id='tooltip-email' {...props}>
                            Diese E-Mail-Adresse ist eventuell fehlerhaft.
                            Bitte überprüfen Sie die Adresse nochmals. Sie
                            können trotzdem fortfahren.
                          </Tooltip>
                        )}
                      </Overlay>
                    )}
                  </small>
                </>
              ) : (
                <p>{participant.email}</p>
              )}
            </td>
          </tr>
          <tr>
            <th>Telefon:</th>
            <td>{participant.phone}</td>
          </tr>
          {!program.requires_family_data ? (
            <>
              <tr>
                {'participant-stage' in institution.custom_configuration &&
                'behavior' in
                institution.custom_configuration['participant-stage'] &&
                institution.custom_configuration['participant-stage'][
                  'behavior'
                  ] === 'custom-school-select' ? (
                  <th>Entsendende Stelle:</th>
                ) : (
                  <th>Schule:</th>
                )}
                <td>
                  {participant.school ? <>{participant.school}</> : '-'}
                </td>
              </tr>
              <tr>
                <th>Erziehungsberechtigter:</th>
                <td>
                  {participant.legal_guardian ? (
                    <>{participant.legal_guardian}</>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            </>
          ) : (
            <tr>
              <th>Daten der Kinder:</th>
              {participant.token ?
                // loaded via code
                <td>Sie haben Daten von {participant.number_of_children} Kind{participant.number_of_children > 1 ? 'ern': ''} angegeben.</td> :
                // new registration
                <td>
                  <p>
                    Sie haben Daten von {participant.children?.length || 0}{' '}
                    Kind{participant.children?.length === 1 ? '' : 'ern'} angegeben.
                  </p>
                  <ul>
                    {participant.children ? (
                      participant.children.map((child, key) => (
                        <li key={key}>
                          {child.name} ({child.date_of_birth})
                        </li>
                      ))
                    ) : (
                      <></>
                    )}
                  </ul>
                </td>
              }
            </tr>
          )}
          <tr>
            <th>Zweiter Erziehungsberechtigter:</th>
            <td>
              {participant.second_legal_guardian
                ? participant.second_legal_guardian
                : '-'}
            </td>
          </tr>
          {program.participant_questions && participant.custom_answers && (
            <>
              {participant.custom_answers.map((question, key) => (
                <tr key={key}>
                  <th>{question.question_label}</th>
                  <td>{question.answer_text}</td>
                </tr>
              ))}
            </>
          )}
          </tbody>
        </Table>
      ) : (
        <Card>
          <Card.Header>
            {`${participant.first_name} ${participant.last_name} ${
              genderSymbols[participant.gender]
            }`}{' '}
            ({participant.date_of_birth})
          </Card.Header>
          <Card.Body>
            <Col className='pb-3 border-bottom d-flex flex-column'>
              <span className='mb-1'>
                <strong>
                  <FontAwesomeIcon icon='home' /> Adresse:
                </strong>{' '}
                <br />
                {participant.street}
                <br />
                {participant.zip_code} {participant.city}{' '}
                {participant.district && '(' + participant.district + ')'}
              </span>
              <span className='mb-1'>
                <strong>
                  <FontAwesomeIcon icon='envelope' /> E-Mail:
                </strong>{' '}
                <br />
                {emailFailed ? (
                  <>
                    <small>
                      <span
                        className='text-danger font-weight-bold'
                        id='email-warning'
                        ref={emailField}
                        onClick={() => setShowEmailWarning(!showEmailWarning)}
                      >
                        {participant.email}
                      </span>
                      {emailField && emailField.current && (
                        <Overlay
                          target={emailField.current}
                          show={showEmailWarning}
                          placement='bottom'
                        >
                          {(props: OverlayInjectedProps) => (
                            <Tooltip id='tooltip-email' {...props}>
                              Diese E-Mail-Adresse ist eventuell fehlerhaft.
                              Bitte überprüfen Sie die Adresse nochmals. Sie
                              können trotzdem fortfahren.
                            </Tooltip>
                          )}
                        </Overlay>
                      )}
                    </small>
                  </>
                ) : (
                  <p>{participant.email}</p>
                )}
              </span>
              <span className='mb-1'>
                <strong>
                  <FontAwesomeIcon icon='phone' /> Telefon:
                </strong>{' '}
                <br />
                {participant.phone}
              </span>
              {participant.disabled && (
                <span>
                  <strong>
                    <FontAwesomeIcon icon={['fab', 'accessible-icon']} />{' '}
                    Behinderung/Beeinträchtigung:
                  </strong>{' '}
                  <br />
                  {participant.disabled ? participant.disabled : 'Ja'}
                </span>
              )}
            </Col>
            {!program.requires_family_data ? (
              <Col className='mt-3 pb-3 border-bottom'>
                <strong>
                  <FontAwesomeIcon icon='school' /> Schule:
                </strong>{' '}
                <br />
                {participant.school ? <>{participant.school}</> : '-'}
                <br />
                <strong>
                  <FontAwesomeIcon icon='user' /> Erziehungsberechtigter:
                </strong>
                <br />
                {participant.legal_guardian ? (
                  <>{participant.legal_guardian}</>
                ) : (
                  '-'
                )}
                <br />
                {participant.second_legal_guardian && (
                  <>
                    <strong>
                      <FontAwesomeIcon icon='user' /> Zweiter
                      Erziehungsberechtigter:
                    </strong>{' '}
                    <br />
                    {participant.second_legal_guardian}
                  </>
                )}
              </Col>
            ) : (
              <Col className='mt-3 pb-3 border-bottom'>
                <strong>
                  <FontAwesomeIcon icon='child' /> Daten der Kinder:
                </strong>{' '}
                <br />
                {participant.token && (
                  <>
                    Sie haben Daten von {participant.number_of_children} Kind{participant.number_of_children > 1 ? 'ern': ''} angegeben.
                  </>
                )}
                <ul>
                  {participant.children.map((child, key) => (
                    <li key={key}>
                      {child.name} ({child.date_of_birth})
                    </li>
                  ))}
                </ul>
              </Col>
            )}
            {program.participant_questions && participant.custom_answers && (
              <Col className='mt-3 pb-3 border-bottom'>
                <strong>
                  <FontAwesomeIcon icon='info-circle' /> Zusätzliche
                  Informationen:
                </strong>
                <ul>
                  {participant.custom_answers.map((question, key) => (
                    <li key={key}>
                      {question.question_label} <br />
                      {question.answer_text ? (
                        question.answer_text
                      ) : (
                        <p className='text-muted'>Keine Angabe</p>
                      )}
                    </li>
                  ))}
                </ul>
              </Col>
            )}
          </Card.Body>
        </Card>
      )}
    </>
  )
}

export default ParticipantData
